import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faBars } from "@fortawesome/free-solid-svg-icons";
import { Offcanvas, Button } from "react-bootstrap";
import logo from "../../assets/images/logo.PNG";
// 🔥 Update import to use the hook
import { useVerticalNavigation } from "../../navigation/vertical/index";
import "./index.css";

const SideNavBar = ({ onCollapseChange }: { onCollapseChange?: (collapsed: boolean) => void }) => {
  const location = useLocation();
  
  // 🔥 Use the hook instead of getVerticalNavigation()
  const { navigation: navigations, isLoading } = useVerticalNavigation();

  const [expandedMenus, setExpandedMenus] = useState<{
    [key: string]: boolean;
  }>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarVisible, setSidebarVisibility] = useState(
    window.innerWidth >= 875
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setSidebarVisibility(window.innerWidth >= 875);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    const newCollapsedState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  const toggleSubMenu = (id: string) => {
    setExpandedMenus((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const isMenuActive = (navItem: any) => {
    if (location.pathname.startsWith(navItem.navLink)) {
      return true;
    }
    if (navItem.subMenu) {
      return navItem.subMenu.some(
        (subNavItem: any) => location.pathname.startsWith(subNavItem.navLink)
      );
    }
    return false;
  };

  const handleNavClick = (e: React.MouseEvent, navItem: any) => {
    // Handle submenu toggle
    if (navItem.subMenu) {
      e.preventDefault();
      toggleSubMenu(navItem.id);
    }
  };

  const renderNavItem = (navItem: any) => {
    // Don't render if not visible (permission check already done in navigation data)
    if (navItem.isVisible === false) {
      return null;
    }

    const isActive = isMenuActive(navItem);
    const backgroundColor = isActive ? "#F2F2F2" : "transparent";

    return (
      <div key={navItem.id} className="desktop-sidebar-links-content-one">
        <div
          className="desktop-sidebar-whole-link-content"
          style={{
            backgroundColor: backgroundColor,
          }}
        >
          <NavLink
            to={navItem.navLink}
            style={{
              backgroundColor: "transparent",
              borderRadius: isActive ? "2px" : "0",
              padding: isActive ? "2% 1%" : "0",
              borderLeft: isActive ? "3px solid #FF1001" : "none",
            }}
            className="desktop-sidebar-link-content"
            onClick={(e) => handleNavClick(e, navItem)}
            title={isSidebarCollapsed ? navItem.title : ""}
          >
            <FontAwesomeIcon
              icon={navItem.icon}
              className="desktop-sidebar-link-icon"
            />
            {!isSidebarCollapsed && <span>{navItem.title}</span>}
          </NavLink>
          {navItem.subMenu && navItem.subMenu.length > 0 && !isSidebarCollapsed && (
            <FontAwesomeIcon
              icon={faAngleDown}
              style={{
                transform: expandedMenus[navItem.id]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                backgroundColor: backgroundColor,
              }}
              className="desktop-sidebar-link-content-expand-arrow"
              onClick={() => toggleSubMenu(navItem.id)}
            />
          )}
        </div>
        {navItem.subMenu &&
          navItem.subMenu.length > 0 &&
          expandedMenus[navItem.id] &&
          !isSidebarCollapsed && (
            <div className="desktop-sidebar-sub-link-content">
              {navItem.subMenu
                .filter((subNavItem: any) => subNavItem.isVisible !== false) // Only render visible sub-items
                .map((subNavItem: any) => {
                  const isSubActive = location.pathname.startsWith(
                    subNavItem.navLink
                  );

                  return (
                    <NavLink
                      key={subNavItem.id}
                      to={subNavItem.navLink}
                      style={{
                        backgroundColor: isSubActive ? "#F2F2F2" : "transparent",
                        borderRadius: isSubActive ? "2px" : "0",
                        padding: "1% 3%",
                      }}
                      className="desktop-sidebar-sub-link"
                    >
                      <div className="desktop-sidebar-sub-link-text">
                        <FontAwesomeIcon
                          icon={subNavItem.icon}
                          style={{
                            border: isSubActive
                              ? "1px solid #FF1001"
                              : "1px solid #384262",
                            backgroundColor: "white",
                          }}
                          className="desktop-sidebar-sub-link-icon"
                        />
                        <span className="desktop-sidebar-sub-link-text-title">
                          {subNavItem.title}
                        </span>
                      </div>
                    </NavLink>
                  );
                })}
            </div>
          )}
      </div>
    );
  };

  // 🔥 Handle loading state
  if (isLoading) {
    return (
      <div className="sidebar-loading">
        <div className="text-center p-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading navigation...</span>
          </div>
          <p className="mt-2">Loading navigation...</p>
        </div>
      </div>
    );
  }

  // Filter visible navigation items (hook already filters, but keep for safety)
  const visibleNavigations = navigations.filter(
    (navItem) => navItem.isVisible !== false
  );

  return (
    <div>
      {isSidebarVisible && (
        <div className={`desktop-sidebar-links-content-one ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          {visibleNavigations.map((navItem, index) => (
            <div key={navItem.id}>
              {/* Show toggle button aligned with the first navigation item (Home) */}
              {index === 0 && (
                <div className="nav-item-with-toggle">
                  {renderNavItem(navItem)}
                  <button
                    className="sidebar-toggle-btn"
                    onClick={toggleSidebarCollapse}
                    title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                  >
                    <FontAwesomeIcon 
                      icon={faBars} 
                      style={{
                        transform: isSidebarCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                        transition: "transform 0.3s ease"
                      }}
                    />
                  </button>
                </div>
              )}
              {/* Render other navigation items normally */}
              {index !== 0 && renderNavItem(navItem)}
            </div>
          ))}
        </div>
      )}
      {!isSidebarVisible && (
        <div className="toggle-btn" onClick={toggleSidebar}>
          <Button variant="warning">
            <FontAwesomeIcon icon={faBars} />
          </Button>
        </div>
      )}
      <Offcanvas show={isSidebarOpen} onHide={toggleSidebar}>
        <Offcanvas.Header closeButton>
          <img src={logo} alt="Logo" className="off-canvas-header-logo" />
        </Offcanvas.Header>
        <hr />
        <Offcanvas.Body className="off-canvas-whole-body">
          {isLoading ? (
            <div className="text-center p-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            visibleNavigations.map(renderNavItem)
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default SideNavBar;
